import { forwardRef, type ReactNode } from 'react';
import { makeStyles, mergeClasses, tokens } from '@coreai/fluentui-react';

const useStyles = makeStyles({
  // Outer shell. Fills the available height of its bounded parent (must
  // sit in a flex column whose chain enforces `min-height: 0` up to a
  // viewport-sized ancestor). Owns the layout contract — consumers don't
  // need to know about flex/overflow plumbing.
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  // Optional top slot. Content-sized so it stays put while the body
  // scrolls underneath — Azure Portal pattern for search + filter rows.
  toolbar: {
    flexShrink: 0,
  },
  // The scrolling viewport. Fills remaining height; rows scroll here.
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Inner flex column lets children that want to stretch (e.g. an
    // empty-state placeholder) fill the available space.
    display: 'flex',
    flexDirection: 'column',
  },
  // Footer slot lives OUTSIDE the scroll viewport. Because it's the last
  // flex item with no `flex` of its own, it sits at the bottom of `root`
  // regardless of how many rows the body has — that's the whole point of
  // this component.
  footer: {
    flexShrink: 0,
  },
  // Visual separator between the (possibly scrolling) body and the
  // pinned footer. Azure Portal uses a faint top border; turn it off
  // with `flushFooter` when the body's own border already provides it.
  footerBorder: {
    borderTopWidth: tokens.strokeWidthThin,
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },
});

export interface DataTablePanelProps {
  /** Optional toolbar pinned to the top of the panel (search, filters). */
  toolbar?: ReactNode;
  /** The scrolling body — typically a `<Table>` (or skeleton / empty state). */
  children: ReactNode;
  /** Optional footer pinned to the bottom of the panel (e.g. `TablePaginationFooter`). */
  footer?: ReactNode;
  /**
   * When false (default), a faint top border separates the footer from
   * the scrolling body. Set true if the body or page already provides
   * a visual divider and you want the footer to sit flush against it.
   */
  flushFooter?: boolean;
  /** Test id for the outer shell. */
  testId?: string;
  /** Test id for the scroll viewport (useful for asserting overflow). */
  bodyTestId?: string;
  /** Test id for the footer slot. */
  footerTestId?: string;
  /** Class merged onto the outer shell. */
  className?: string;
}

/**
 * Layout shell for a data table whose pagination footer must always sit
 * at the bottom of the available space — even when the table has few
 * rows. The footer lives outside the scroll viewport, so it's pinned by
 * flex layout, not by `position: sticky` (which only kicks in once the
 * container actually overflows).
 *
 * The panel itself must be placed in a bounded flex column — the parent
 * chain needs `min-height: 0; overflow: hidden` all the way up to a
 * viewport-sized ancestor, otherwise the panel will grow with its
 * content and the footer will float with it.
 *
 * @example
 * ```tsx
 * <DataTablePanel
 *   toolbar={<SearchAndFilters />}
 *   footer={<TablePaginationFooter ... />}
 * >
 *   <Table>...</Table>
 * </DataTablePanel>
 * ```
 */
export const DataTablePanel = forwardRef<HTMLDivElement, DataTablePanelProps>(
  function DataTablePanel(
    { toolbar, children, footer, flushFooter = false, testId, bodyTestId, footerTestId, className },
    ref,
  ) {
    const styles = useStyles();
    return (
      <div ref={ref} className={mergeClasses(styles.root, className)} data-testid={testId}>
        {toolbar != null && <div className={styles.toolbar}>{toolbar}</div>}
        <div className={styles.body} data-testid={bodyTestId}>
          {children}
        </div>
        {footer != null && (
          <div
            className={mergeClasses(styles.footer, !flushFooter && styles.footerBorder)}
            data-testid={footerTestId}
          >
            {footer}
          </div>
        )}
      </div>
    );
  },
);
